import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useMutation, useQueries } from '@tanstack/react-query';
import type { PersonRelationshipResponse, RelationshipType } from '@tabita/contracts';
import { useAuth } from '@/auth/auth-context';
import { useI18n } from '@/i18n/i18n-provider';
import { peopleApi } from '@/services/people.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button } from '@/components/ui';
import { Avatar } from '@/components/avatar';
import { PersonPicker } from './person-picker';

/**
 * The eight relationship types with clear generational meaning — the ones
 * this tree can actually place on a level. `FRIEND`/`CAREGIVER`/`OTHER`
 * still show on the flat `RelationshipsCard` in the Info tab; they have no
 * "up" or "down" here, so this tab never lists or offers them.
 */
const FAMILY_RELATIONSHIP_TYPES: readonly RelationshipType[] = [
  'GRANDPARENT',
  'PARENT',
  'AUNT_UNCLE',
  'GUARDIAN',
  'SIBLING',
  'SPOUSE',
  'CHILD',
  'GRANDCHILD',
];

/**
 * Only the pairs the type system actually models get an automatic inverse —
 * `createRelationship`'s own `inverseType` doc example (`GUARDIAN` /
 * `CHILD`) is the precedent this follows. `AUNT_UNCLE` has no modeled
 * inverse (no `NIECE_NEPHEW` type exists), so it's left for the backend's
 * own fallback — the same gap `RelationshipsCard` already lives with today.
 */
const INVERSE_TYPE: Partial<Record<RelationshipType, RelationshipType>> = {
  PARENT: 'CHILD',
  CHILD: 'PARENT',
  GRANDPARENT: 'GRANDCHILD',
  GRANDCHILD: 'GRANDPARENT',
  GUARDIAN: 'CHILD',
  SIBLING: 'SIBLING',
  SPOUSE: 'SPOUSE',
};

/**
 * One tree node. `relationshipId` is only set for a relationship recorded
 * directly on this person — a grandchild found by walking a child's own
 * children has none, since the fact lives on that child's record instead.
 * Those can't be edited or deleted from here; `via` names whose direct
 * relationship actually carries the fact, so the chip can say so rather
 * than silently offering no actions.
 */
interface TreeMember {
  readonly relatedPersonId: string;
  readonly relatedPersonDisplayName: string;
  readonly relatedPersonPhotoFileId: string | null;
  readonly relationshipId: string | null;
  readonly via: string | null;
}

/** Everything reachable through one specific direct parent: their own
 *  parents (this person's grandparents on that side) and their own
 *  siblings (this person's aunts/uncles on that side) — never mixed with
 *  what the *other* parent's own side reveals, which is the actual bug
 *  report this shape exists to fix: a shared "grandparents" pile made it
 *  look like a child belonged to whichever parent the tree happened to
 *  center under. */
interface ParentBranch {
  readonly parent: TreeMember;
  readonly grandparents: TreeMember[];
  readonly auntsUncles: TreeMember[];
}

/** Everything reachable through one specific direct child: their own
 *  children (this person's grandchildren through that one child only). */
interface ChildBranch {
  readonly child: TreeMember;
  readonly grandchildren: TreeMember[];
}

function toDirectMember(relationship: PersonRelationshipResponse): TreeMember {
  return {
    relatedPersonId: relationship.relatedPersonId,
    relatedPersonDisplayName: relationship.relatedPersonDisplayName,
    relatedPersonPhotoFileId: relationship.relatedPersonPhotoFileId,
    relationshipId: relationship.id,
    via: null,
  };
}

function toDerivedMember(found: PersonRelationshipResponse, viaDisplayName: string): TreeMember {
  return {
    relatedPersonId: found.relatedPersonId,
    relatedPersonDisplayName: found.relatedPersonDisplayName,
    relatedPersonPhotoFileId: found.relatedPersonPhotoFileId,
    relationshipId: null,
    via: viaDisplayName,
  };
}

function describeError(error: unknown, t: ReturnType<typeof useI18n>['t']): string {
  return error instanceof ApiError
    ? t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
    : t('errors.INTERNAL_ERROR');
}

/**
 * One tree node, hoisted out of `FamilyTreeCard` on purpose: a component
 * defined inside another component's body is a new function identity every
 * render, and React remounts (not updates) an element whose type changed
 * identity — every parent/child relative's own relationship query
 * resolving would otherwise blow away and recreate every chip on the page,
 * dropping whatever the resulting click was headed for.
 */
function Chip({
  member,
  t,
  canCreate,
  canDelete,
  isRetyping,
  currentType,
  retypePending,
  removePending,
  onStartRetype,
  onChangeType,
  onStopRetype,
  onDelete,
}: {
  readonly member: TreeMember;
  readonly t: ReturnType<typeof useI18n>['t'];
  readonly canCreate: boolean;
  readonly canDelete: boolean;
  readonly isRetyping: boolean;
  readonly currentType: RelationshipType | undefined;
  readonly retypePending: boolean;
  readonly removePending: boolean;
  readonly onStartRetype: () => void;
  readonly onChangeType: (newType: RelationshipType) => void;
  readonly onStopRetype: () => void;
  readonly onDelete: () => void;
}): ReactNode {
  return (
    <li className="family-tree-chip">
      <div className="family-tree-chip-info">
        <Link to={`/people/${member.relatedPersonId}`}>
          <Avatar
            photoFileId={member.relatedPersonPhotoFileId}
            displayName={member.relatedPersonDisplayName}
            size={56}
          />
        </Link>
        <Link to={`/people/${member.relatedPersonId}`} className="family-tree-chip-name">
          {member.relatedPersonDisplayName}
        </Link>
        {member.via && (
          <span className="family-tree-chip-via">
            {t('people.familyTree.via', { values: { name: member.via } })}
          </span>
        )}
      </div>
      {isRetyping && currentType ? (
        <select
          className="field-input"
          aria-label={t('people.familyTree.editType')}
          autoFocus
          value={currentType}
          disabled={retypePending}
          onChange={(event) => onChangeType(event.target.value as RelationshipType)}
          onBlur={onStopRetype}
        >
          {FAMILY_RELATIONSHIP_TYPES.map((value) => (
            <option key={value} value={value}>
              {t(`people.relationship.type.${value}`)}
            </option>
          ))}
        </select>
      ) : (
        member.relationshipId !== null && (
          <div className="family-tree-chip-actions">
            {canCreate && (
              <Button
                variant="ghost"
                aria-label={t('people.familyTree.editType')}
                onClick={onStartRetype}
              >
                {t('common.actions.edit')}
              </Button>
            )}
            {canDelete && (
              <Button variant="ghost" loading={removePending} onClick={onDelete}>
                {t('common.actions.delete')}
              </Button>
            )}
          </div>
        )
      )}
    </li>
  );
}

function Cluster({
  label,
  items,
  renderItem,
}: {
  readonly label: string;
  readonly items: readonly TreeMember[];
  readonly renderItem: (member: TreeMember) => ReactNode;
}): ReactNode {
  if (items.length === 0) return null;
  return (
    <div className="family-tree-cluster">
      <span className="family-tree-cluster-title">{label}</span>
      <ul className="family-tree-cluster-list">{items.map(renderItem)}</ul>
    </div>
  );
}

/** One parent, their own parents above them, and their own siblings beside
 *  them — a self-contained branch, so its internal connector always
 *  centers on *this* parent, never on the other parent's branch next to
 *  it or on any sibling of this person's own generation. */
function ParentBranchView({
  branch,
  renderChip,
}: {
  readonly branch: ParentBranch;
  readonly renderChip: (member: TreeMember) => ReactNode;
}): ReactNode {
  const hasGrandparents = branch.grandparents.length > 0;
  return (
    <div className="family-tree-branch">
      {hasGrandparents && (
        <>
          <ul className="family-tree-cluster-list family-tree-branch-row">
            {branch.grandparents[0] && renderChip(branch.grandparents[0])}
            {branch.grandparents.length === 2 && (
              <li className="family-tree-couple-link-item" aria-hidden="true">
                <span className="family-tree-couple-link" />
              </li>
            )}
            {branch.grandparents.slice(1).map(renderChip)}
          </ul>
          <div className="family-tree-row-connector" />
        </>
      )}
      <ul className="family-tree-cluster-list family-tree-branch-row">
        {renderChip(branch.parent)}
        {branch.auntsUncles.map(renderChip)}
      </ul>
    </div>
  );
}

/** One child, and their own children (this person's grandchildren through
 *  that child alone) hanging directly beneath them — never beneath the
 *  child's siblings, and never beneath this person's spouse. */
function ChildBranchView({
  branch,
  renderChip,
}: {
  readonly branch: ChildBranch;
  readonly renderChip: (member: TreeMember) => ReactNode;
}): ReactNode {
  const hasGrandchildren = branch.grandchildren.length > 0;
  return (
    <div className="family-tree-branch">
      <ul className="family-tree-cluster-list family-tree-branch-row">
        {renderChip(branch.child)}
      </ul>
      {hasGrandchildren && (
        <>
          <div className="family-tree-row-connector" />
          <ul className="family-tree-cluster-list family-tree-branch-row">
            {branch.grandchildren.map(renderChip)}
          </ul>
        </>
      )}
    </div>
  );
}

/**
 * A generational family tree — grandparents down to grandchildren, plus
 * aunts/uncles and guardians alongside parents — built from the same
 * `PersonRelationship` rows `RelationshipsCard` already lists flat, PLUS
 * one level of derived reach through them.
 *
 * `PersonRelationship` only ever stores a direct, one-hop fact ("Ana's
 * child is Bruno"), never a computed one — nothing records "Ana's
 * grandchild is Carla" on Ana's own row when Bruno's own child is Carla.
 * Without walking that second hop, a person's grandchildren, grandparents
 * and aunts/uncles would only ever appear if someone manually duplicated
 * the same fact again one level up, which nobody does — so this fetches
 * each direct parent's and child's own relationships and folds in whatever
 * they reveal, without asking anyone to re-enter it.
 *
 * The tree branches by the *specific* parent or child a second-degree
 * relative was found through (`ParentBranch`/`ChildBranch`), rather than
 * pooling every grandparent or every grandchild into one shared row: two
 * parents' own families rendered side by side as one undifferentiated
 * group looked like they belonged to whichever parent the merged group
 * happened to visually center under, and the same was true of children
 * hanging under a shared row instead of under this person and their
 * spouse specifically.
 */
export function FamilyTreeCard({
  personId,
  personDisplayName,
  personPhotoFileId,
  relationships,
  onChanged,
}: {
  readonly personId: string;
  readonly personDisplayName: string;
  readonly personPhotoFileId: string | null;
  readonly relationships: readonly PersonRelationshipResponse[];
  readonly onChanged: () => void;
}) {
  const { t } = useI18n();
  const { can } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [addType, setAddType] = useState<RelationshipType>('CHILD');
  const [retypingId, setRetypingId] = useState<string | null>(null);

  const canCreate = can('people.relationship.create');
  const canDelete = can('people.relationship.delete');
  const canRead = can('people.relationship.read');

  const parentRelations = relationships.filter((r) => r.relationshipType === 'PARENT');
  const childRelations = relationships.filter((r) => r.relationshipType === 'CHILD');
  const directGrandparents = relationships.filter((r) => r.relationshipType === 'GRANDPARENT');
  const directAuntsUncles = relationships.filter((r) => r.relationshipType === 'AUNT_UNCLE');
  const directGrandchildren = relationships.filter((r) => r.relationshipType === 'GRANDCHILD');
  const guardians = relationships.filter((r) => r.relationshipType === 'GUARDIAN');
  const siblings = relationships.filter((r) => r.relationshipType === 'SIBLING');
  const spouses = relationships.filter((r) => r.relationshipType === 'SPOUSE');

  // One request per direct parent/child, in parallel — bounded by how many
  // parents and children a person actually has, never the whole tenant.
  const parentQueries = useQueries({
    queries: parentRelations.map((relation) => ({
      queryKey: ['people', relation.relatedPersonId, 'relationships'],
      queryFn: () => peopleApi.listRelationships(relation.relatedPersonId),
      enabled: canRead,
    })),
  });
  const childQueries = useQueries({
    queries: childRelations.map((relation) => ({
      queryKey: ['people', relation.relatedPersonId, 'relationships'],
      queryFn: () => peopleApi.listRelationships(relation.relatedPersonId),
      enabled: canRead,
    })),
  });

  const create = useMutation({
    mutationFn: (input: { relatedPersonId: string; relationshipType: RelationshipType }) =>
      peopleApi.createRelationship(personId, {
        relatedPersonId: input.relatedPersonId,
        relationshipType: input.relationshipType,
        inverseType: INVERSE_TYPE[input.relationshipType] ?? null,
      }),
    onSuccess: () => {
      setError(null);
      onChanged();
    },
    onError: (err: unknown) => setError(describeError(err, t)),
  });

  const remove = useMutation({
    mutationFn: (relationshipId: string) => peopleApi.deleteRelationship(relationshipId),
    onSuccess: () => {
      setError(null);
      onChanged();
    },
    onError: (err: unknown) => setError(describeError(err, t)),
  });

  // There is no PATCH for a relationship's type — `PersonRelationship` is
  // "deliberately thin: ... a wrong relationship is deleted and re-created,
  // not corrected through a series of intermediate states" (see the
  // domain entity). "Editing" here is that same delete-then-create,
  // just presented as one action on one chip.
  const retype = useMutation({
    mutationFn: async (input: {
      relationshipId: string;
      relatedPersonId: string;
      newType: RelationshipType;
    }) => {
      await peopleApi.deleteRelationship(input.relationshipId);
      return peopleApi.createRelationship(personId, {
        relatedPersonId: input.relatedPersonId,
        relationshipType: input.newType,
        inverseType: INVERSE_TYPE[input.newType] ?? null,
      });
    },
    onSuccess: () => {
      setError(null);
      setRetypingId(null);
      onChanged();
    },
    onError: (err: unknown) => setError(describeError(err, t)),
  });

  if (!canRead) return null;

  // A person directly recorded (e.g. a grandparent added straight onto
  // this person, with no parent in between on record) can't be placed in
  // any specific branch — it's tracked here so it's never silently
  // dropped, and shown in its own unattributed group instead.
  const claimedIds = new Set<string>();

  // A direct relationship on this person always wins over a derived guess
  // at the same person, same as before — but now that guess is also
  // attributed to a specific branch, so preferring the direct record (kept
  // editable, its real `relationshipId` intact) must not mean losing that
  // attribution and dropping the person into the unattributed group.
  const directGrandparentById = new Map(directGrandparents.map((r) => [r.relatedPersonId, r]));
  const directAuntUncleById = new Map(directAuntsUncles.map((r) => [r.relatedPersonId, r]));
  const directGrandchildById = new Map(directGrandchildren.map((r) => [r.relatedPersonId, r]));

  const parentBranches: ParentBranch[] = parentRelations.map((parentRelation, index) => {
    claimedIds.add(parentRelation.relatedPersonId);
    const grandparents: TreeMember[] = [];
    const auntsUncles: TreeMember[] = [];
    for (const found of parentQueries[index]?.data ?? []) {
      if (found.relatedPersonId === personId) continue;
      if (found.relationshipType === 'PARENT') {
        const direct = directGrandparentById.get(found.relatedPersonId);
        grandparents.push(
          direct
            ? toDirectMember(direct)
            : toDerivedMember(found, parentRelation.relatedPersonDisplayName),
        );
        claimedIds.add(found.relatedPersonId);
      } else if (found.relationshipType === 'SIBLING') {
        const direct = directAuntUncleById.get(found.relatedPersonId);
        auntsUncles.push(
          direct
            ? toDirectMember(direct)
            : toDerivedMember(found, parentRelation.relatedPersonDisplayName),
        );
        claimedIds.add(found.relatedPersonId);
      }
    }
    return { parent: toDirectMember(parentRelation), grandparents, auntsUncles };
  });

  const childBranches: ChildBranch[] = childRelations.map((childRelation, index) => {
    const grandchildren: TreeMember[] = [];
    for (const found of childQueries[index]?.data ?? []) {
      if (found.relatedPersonId === personId) continue;
      if (found.relationshipType !== 'CHILD') continue;
      const direct = directGrandchildById.get(found.relatedPersonId);
      grandchildren.push(
        direct
          ? toDirectMember(direct)
          : toDerivedMember(found, childRelation.relatedPersonDisplayName),
      );
      claimedIds.add(found.relatedPersonId);
    }
    return { child: toDirectMember(childRelation), grandchildren };
  });

  // Direct grandparents/aunts-uncles/grandchildren already reached through
  // a parent or child branch above are that branch's, not repeated here —
  // only ones with no known branch (no intermediate relationship on
  // record) fall back to an unattributed group.
  const unattributedGrandparents = directGrandparents
    .filter((r) => !claimedIds.has(r.relatedPersonId))
    .map(toDirectMember);
  const unattributedAuntsUncles = directAuntsUncles
    .filter((r) => !claimedIds.has(r.relatedPersonId))
    .map(toDirectMember);
  const unattributedGrandchildren = directGrandchildren
    .filter((r) => !claimedIds.has(r.relatedPersonId))
    .map(toDirectMember);
  const guardianMembers = guardians.map(toDirectMember);
  const siblingMembers = siblings.map(toDirectMember);
  const spouseMembers = spouses.map(toDirectMember);

  const hasAncestors =
    parentBranches.length > 0 ||
    guardianMembers.length > 0 ||
    unattributedGrandparents.length > 0 ||
    unattributedAuntsUncles.length > 0;
  const hasDescendants = childBranches.length > 0 || unattributedGrandchildren.length > 0;
  const hasAnyMember =
    hasAncestors || hasDescendants || siblingMembers.length > 0 || spouseMembers.length > 0;

  function renderChip(member: TreeMember): ReactNode {
    const currentType = relationships.find((r) => r.id === member.relationshipId)?.relationshipType;
    return (
      <Chip
        key={member.relatedPersonId}
        member={member}
        t={t}
        canCreate={canCreate}
        canDelete={canDelete}
        isRetyping={member.relationshipId !== null && retypingId === member.relationshipId}
        currentType={currentType}
        retypePending={retype.isPending}
        removePending={remove.isPending && remove.variables === member.relationshipId}
        onStartRetype={() => setRetypingId(member.relationshipId)}
        onChangeType={(newType) =>
          retype.mutate({
            relationshipId: member.relationshipId!,
            relatedPersonId: member.relatedPersonId,
            newType,
          })
        }
        onStopRetype={() => setRetypingId(null)}
        onDelete={() => remove.mutate(member.relationshipId!)}
      />
    );
  }

  return (
    <div>
      <p className="field-hint">{t('people.familyTree.subtitle')}</p>

      {error && <Alert tone="danger">{error}</Alert>}

      {!hasAnyMember ? (
        <p className="cell-empty">{t('people.familyTree.empty')}</p>
      ) : (
        <div className="family-tree">
          {hasAncestors && (
            <>
              <div className="family-tree-row">
                {parentBranches.map((branch) => (
                  <ParentBranchView
                    key={branch.parent.relatedPersonId}
                    branch={branch}
                    renderChip={renderChip}
                  />
                ))}
                <Cluster
                  label={t('people.familyTree.levels.guardians')}
                  items={guardianMembers}
                  renderItem={renderChip}
                />
                <Cluster
                  label={t('people.familyTree.levels.grandparents')}
                  items={unattributedGrandparents}
                  renderItem={renderChip}
                />
                <Cluster
                  label={t('people.familyTree.levels.auntsUncles')}
                  items={unattributedAuntsUncles}
                  renderItem={renderChip}
                />
              </div>
              <div className="family-tree-row-connector" />
            </>
          )}

          <div className="family-tree-row">
            <Cluster
              label={t('people.familyTree.levels.siblings')}
              items={siblingMembers}
              renderItem={renderChip}
            />
            <div className="family-tree-branch">
              <ul className="family-tree-cluster-list family-tree-branch-row">
                <li className="family-tree-chip is-self">
                  <div className="family-tree-chip-info">
                    <Avatar
                      photoFileId={personPhotoFileId}
                      displayName={personDisplayName}
                      size={56}
                    />
                    <span className="family-tree-chip-name">{personDisplayName}</span>
                  </div>
                </li>
                {spouseMembers.length > 0 && (
                  <li className="family-tree-couple-link-item" aria-hidden="true">
                    <span className="family-tree-couple-link" />
                  </li>
                )}
                {spouseMembers.map(renderChip)}
              </ul>
              {hasDescendants && (
                <>
                  <div className="family-tree-row-connector" />
                  <div className="family-tree-row">
                    {childBranches.map((branch) => (
                      <ChildBranchView
                        key={branch.child.relatedPersonId}
                        branch={branch}
                        renderChip={renderChip}
                      />
                    ))}
                    <Cluster
                      label={t('people.familyTree.levels.grandchildren')}
                      items={unattributedGrandchildren}
                      renderItem={renderChip}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {canCreate && (
        <div className="form-grid">
          <div className="field">
            <label className="field-label" htmlFor="family-tree-type">
              {t('people.relationship.typeLabel')}
            </label>
            <select
              id="family-tree-type"
              className="field-input"
              value={addType}
              onChange={(event) => setAddType(event.target.value as RelationshipType)}
            >
              {FAMILY_RELATIONSHIP_TYPES.map((value) => (
                <option key={value} value={value}>
                  {t(`people.relationship.type.${value}`)}
                </option>
              ))}
            </select>
          </div>
          <PersonPicker
            label={t('people.familyTree.addMember')}
            placeholder={t('groups.group.choosePerson')}
            exclude={[personId, ...relationships.map((r) => r.relatedPersonId)]}
            onPick={(relatedPersonId) =>
              create.mutate({ relatedPersonId, relationshipType: addType })
            }
          />
        </div>
      )}
    </div>
  );
}
